import { useTranslation } from 'react-i18next'

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Box,
  Typography,
  ListItemIcon,
} from '@mui/material'

interface WaterQualityValue {
  value: number | null
  isOutOfRange: boolean
}

interface QualityTodayTableProps {
  data: {
    morning: Record<string, WaterQualityValue>
    evening: Record<string, WaterQualityValue>
  }
  tableNumber: number
}

export default function QualityTodayTable({ data, tableNumber }: QualityTodayTableProps) {
  const { t } = useTranslation(['water'])

  const fields = [
    {
      key: 'pH',
      label: 'pH',
      icon: '/static/icons/layout/medicine.svg',
      unit: '',
    },
    {
      key: 'alkali',
      label: 'อัลคาไลน์',
      icon: '/static/icons/layout/water-quality.svg',
      unit: ' ppm',
    },
    {
      key: 'temperature',
      label: 'อุณหภูมิ',
      icon: '/static/icons/layout/temperature.svg',
      unit: ' °C',
    },
    {
      key: 'nitrite',
      label: 'ไนไตรท์',
      icon: '/static/icons/layout/watering-soil.svg',
      unit: ' ppm',
    },
    { key: 'DO', label: 'ออกซิเจน', icon: '/static/icons/layout/oxygen.svg', unit: 'ppm' },
    { key: 'ammonia', label: 'แอมโมเนีย', icon: '/static/icons/layout/ammonia.svg', unit: ' ppm' },
    {
      key: 'terbidity',
      label: 'ความขุ่น',
      icon: '/static/icons/layout/salinity.svg',
      unit: ' NTU',
    },
    {
      key: 'salinity',
      label: 'ความเค็ม',
      icon: '/static/icons/layout/watering-soil.svg',
      unit: ' ppt',
    },
  ]

  const startIndex = tableNumber === 1 ? 0 : 4
  const endIndex = tableNumber === 1 ? 4 : 8

  const tableData = fields.slice(startIndex, endIndex).map((f) => {
    const morningData = data.morning[f.key]
    const eveningData = data.evening[f.key]

    return {
      label: f.label,
      icon: f.icon,
      morning: morningData?.value != null ? morningData.value : '-',
      evening: eveningData?.value != null ? eveningData.value : '-',
      morningOutOfRange: morningData?.isOutOfRange || false,
      eveningOutOfRange: eveningData?.isOutOfRange || false,
      unit: f.unit,
    }
  })

  return (
    <Box mx="auto">
      {/* Header */}
      <Box display="grid" gridTemplateColumns="1fr 1fr 1fr" textAlign="center">
        <Box></Box>
        <Box sx={{ backgroundColor: 'primary.main', color: '#fff', py: 1, borderTopLeftRadius: 8 }}>
          <Typography fontSize={18} fontWeight={600}>
            {t('water:morning')}
          </Typography>
        </Box>
        <Box
          sx={{ backgroundColor: 'primary.main', color: '#fff', py: 1, borderTopRightRadius: 8 }}
        >
          <Typography fontSize={18} fontWeight={600}>
            {t('water:evening')}
          </Typography>
        </Box>
      </Box>

      <TableContainer component={Paper} elevation={3} sx={{ borderRadius: '16px' }}>
        <Table>
          <TableBody>
            {tableData.map((item, idx) => (
              <TableRow key={idx}>
                {/* Icon + Label */}
                <TableCell align="center" sx={{ border: '1px solid #0000000F', width: 185 }}>
                  <Box display="flex" flexDirection="column" alignItems="start" pl={1} py={1}>
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        justifyContent: 'center',
                      }}
                    >
                      <Box
                        sx={{
                          backgroundColor: '#F5F5F5',
                          width: 40,
                          height: 40,
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          borderRadius: 1,
                        }}
                      >
                        <img src={item.icon} width={24} height={24} />
                      </Box>
                    </ListItemIcon>

                    <Typography fontSize={18} color="gray">
                      {item.label}
                    </Typography>
                  </Box>
                </TableCell>

                {/* Morning */}
                <TableCell align="center" sx={{ border: '1px solid #0000000F', width: 185 }}>
                  <Box display="flex" justifyContent="center" alignItems="baseline" gap={0.5}>
                    <Typography 
                      color={item.morningOutOfRange ? 'error' : 'common.black'} 
                      fontWeight={600} 
                      fontSize={32}
                    >
                      {item.morning}
                    </Typography>
                    <Typography 
                      color={item.morningOutOfRange ? 'error' : 'common.black'} 
                      fontWeight={600} 
                      fontSize={18}
                    >
                      {item.unit}
                    </Typography>
                  </Box>
                </TableCell>

                {/* Evening */}
                <TableCell align="center" sx={{ border: '1px solid #0000000F', width: 185 }}>
                  <Box display="flex" justifyContent="center" alignItems="baseline" gap={0.5}>
                    <Typography 
                      color={item.eveningOutOfRange ? 'error' : 'common.black'} 
                      fontWeight={600} 
                      fontSize={32}
                    >
                      {item.evening}
                    </Typography>
                    <Typography 
                      color={item.eveningOutOfRange ? 'error' : 'common.black'} 
                      fontWeight={600} 
                      fontSize={18}
                    >
                      {item.unit}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}